import React, { useState, useMemo, useCallback } from 'react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell, ReferenceLine,
} from 'recharts';
import { INDUSTRIES, getVariables, getPillars, calcScore, analyseChange, formatMoney, scenarioSummary } from './engine/simulator';
import logo from './logo.jpg';
import s from './App.module.css';

const BOOKING = 'https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ10IdK3gHcmcy8zUNpGqcmfX3031F1MU8pQ41-hUknRDrAsqpeieCj1cenFDj0nIhtgVsvHFpLv';

const TT = { background:'#080e1c', border:'1px solid #162240', borderRadius:8, color:'#f4f8ff', fontSize:11, padding:'7px 11px' };

function Ring({ score, size=80 }) {
  const r = (size-8)/2, circ = 2*Math.PI*r;
  const pct = Math.min(100,Math.max(0,score))/100;
  const col = score>=70?'#00c97d':score>=55?'#f5a623':'#c41c1c';
  return (
    <svg width={size} height={size}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#162240" strokeWidth={6}/>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={col} strokeWidth={6}
        strokeDasharray={`${pct*circ} ${circ}`} strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`}
        style={{transition:'stroke-dasharray 0.4s cubic-bezier(.4,0,.2,1), stroke 0.3s'}}/>
      <text x={size/2} y={size/2} textAnchor="middle" dominantBaseline="central"
        fill={col} fontSize={size>70?20:14} fontFamily="Syne,sans-serif" fontWeight="800"
        style={{transition:'fill 0.3s'}}>{score}</text>
    </svg>
  );
}

function Slider({ variable:vr, value, onChange, isChanged }) {
  const { min, max, step, unit, label, benchmark, direction, description } = vr;
  const pct = Math.max(0,Math.min(100,((value-min)/(max-min))*100));
  const bPct = Math.max(0,Math.min(100,((benchmark-min)/(max-min))*100));
  const atPar = Math.abs(value-benchmark)<step;
  const better = direction==='higher'?value>=benchmark:value<=benchmark;
  const col = atPar?'#00c97d':better?'#00c97d':'#c41c1c';
  const clamp = v => Math.max(min, Math.min(max, v));
  const nudge = d => onChange(clamp(Number((value + d * step).toFixed(4))));
  return (
    <div className={`${s.sliderItem} ${isChanged?s.sliderItemChanged:''}`}>
      <div className={s.sliderHead}>
        <div className={s.sliderHeadLeft}>
          <div className={s.sliderLabel}>{label}</div>
          <div className={s.sliderDesc}>{description}</div>
        </div>
        <div className={s.sliderRight}>
          <div className={s.sliderValueRow}>
            <button type="button" className={s.sliderNudge} onClick={()=>nudge(-1)} aria-label={`Decrease ${label}`} disabled={value<=min}>−</button>
            <span className={s.sliderValue} style={{color:col}}>{value}{unit}</span>
            <button type="button" className={s.sliderNudge} onClick={()=>nudge(1)} aria-label={`Increase ${label}`} disabled={value>=max}>+</button>
          </div>
          {atPar
            ? <span className={`${s.sliderBench} ${s.sliderBenchAtPar}`}>✓ at benchmark</span>
            : <button type="button" className={s.sliderBenchBtn} onClick={()=>onChange(benchmark)} title="Snap to industry benchmark">
                bench: {benchmark}{unit} <span className={s.sliderBenchSnap}>↺</span>
              </button>}
        </div>
      </div>
      <div className={s.trackWrap}>
        <div className={s.trackBase}/>
        <div className={s.trackFill} style={{width:`${pct}%`,background:col}}/>
        <div className={s.benchPin} style={{left:`${bPct}%`}} title={`Benchmark: ${benchmark}${unit}`}/>
        <div className={s.sliderThumb} style={{left:`${pct}%`,background:col,boxShadow:`0 0 0 4px ${col}22, 0 0 12px ${col}66`}}/>
        <input type="range" className={s.sliderInput} min={min} max={max} step={step} value={value}
          aria-label={label}
          onChange={e=>onChange(Number(e.target.value))}/>
      </div>
      <div className={s.sliderRange}>
        <span>{min}{unit}</span>
        <span className={s.sliderRangeHint}>
          {direction==='higher'?'higher is better':'lower is better'}
        </span>
        <span>{max}{unit}</span>
      </div>
    </div>
  );
}

function PillarAccordion({ pillar, vars, values, base, onChange, baseScore, newScore, open, onToggle }) {
  const changed = vars.filter(v=>values[v.key]!==base[v.key]).length;
  const scoreCol = newScore>=70?'#00c97d':newScore>=55?'#f5a623':'#c41c1c';
  return (
    <div className={s.pillar}>
      <button className={`${s.pillarBtn} ${open?s.pillarBtnOpen:''}`} onClick={onToggle}>
        <div className={s.pillarAccent} style={{background:pillar.color}}/>
        <div className={s.pillarLetter} style={{background:pillar.dim,color:pillar.color,borderColor:pillar.color+'33'}}>{pillar.letter}</div>
        <div className={s.pillarMeta}>
          <div className={s.pillarName}>{pillar.label}</div>
          <div className={s.pillarKey}>{vars.length>0?`${vars.length} variable${vars.length!==1?'s':''}`:'computed pillar'}</div>
        </div>
        <div className={s.pillarRight}>
          {changed>0&&<span className={s.pillarChangedBadge}>{changed}</span>}
          <span className={s.pillarScore} style={{color:scoreCol}}>{newScore}</span>
          <span className={s.pillarChevron}>{open?'▲':'▼'}</span>
        </div>
      </button>
      {open&&(
        <div className={s.pillarBody}>
          {pillar.keyFocus&&(
            <div className={s.pillarFocus}>
              <div className={s.pillarFocusLabel} style={{color:pillar.color}}>Key Focus</div>
              <div className={s.pillarFocusText}>{pillar.keyFocus}</div>
              {pillar.action&&<div className={s.pillarFocusAction}>{pillar.action}</div>}
            </div>
          )}
          {vars.length===0
            ? <div className={s.pillarEmpty}>
                <div className={s.pillarEmptyIcon} style={{color:pillar.color}}>◈</div>
                <div className={s.pillarEmptyTitle}>Computed from gap analysis</div>
                <div className={s.pillarEmptyText}>This pillar's score is derived from how the variables in the Revenue, Operational and Tactics pillars compare against your industry benchmarks. Adjust those levers to see this score respond — high values here indicate large untapped ROI.</div>
              </div>
            : vars.map(v=>(
              <Slider key={v.key} variable={v} value={values[v.key]}
                isChanged={values[v.key]!==base[v.key]}
                onChange={val=>onChange(v.key,val)}/>
            ))}
        </div>
      )}
    </div>
  );
}

function AnalysisCard({ analysis }) {
  if (!analysis) return null;
  const tm = {
    positive:{color:'#00c97d',label:'IMPROVEMENT',css:s.analysisPos},
    negative:{color:'#c41c1c',label:'RISK SIGNAL',css:s.analysisNeg},
    neutral:{color:'#f5a623',label:'MIXED SIGNAL',css:s.analysisNeutral},
  };
  const t = tm[analysis.type]||tm.neutral;
  return (
    <div className={`${s.analysisCard} ${t.css}`}>
      <div className={s.analysisCardInner}>
        <div className={s.analysisCardType}>
          <div className={s.analysisTypeIcon} style={{background:t.color}}/>
          <span className={s.analysisTypeLabel} style={{color:t.color}}>{t.label}</span>
        </div>
        <div className={s.analysisHeadline} style={{borderLeftColor:t.color,color:t.color}}>{analysis.headline}</div>
        <div className={s.analysisInsight}>{analysis.insight}</div>
      </div>
    </div>
  );
}

export default function App() {
  const [industry, setIndustry] = useState('SaaS');
  const [acv, setAcv] = useState(50000);
  const [size, setSize] = useState(200);
  const [openPillar, setOpenPillar] = useState('revenue');
  const [activeChart, setActiveChart] = useState('radar');
  const [lastKey, setLastKey] = useState(null);

  const ind = INDUSTRIES[industry]||INDUSTRIES['SaaS'];
  const variables = useMemo(()=>getVariables(industry),[industry]);
  const PILLARS = useMemo(()=>getPillars(industry),[industry]);

  const buildBase = useCallback((vars,a,sz)=>{
    const d={_acv:a,_size:sz};
    Object.values(vars).flat().forEach(v=>{d[v.key]=v.default;});
    return d;
  },[]);

  const [values, setValues] = useState(()=>buildBase(getVariables('SaaS'),50000,200));
  const base = useMemo(()=>buildBase(variables,acv,size),[variables,acv,size,buildBase]);

  const resetIndustry = newInd => {
    setIndustry(newInd);
    setValues(buildBase(getVariables(newInd),acv,size));
    setLastKey(null);
  };
  const handleChange = (key,val) => { setValues(p=>({...p,[key]:val})); setLastKey(key); };
  const handleReset = () => { setValues({...base,_acv:acv,_size:size}); setLastKey(null); };

  const cur = useMemo(()=>calcScore({...base,_acv:acv,_size:size},industry),[base,acv,size,industry]);
  const nxt = useMemo(()=>calcScore({...values,_acv:acv,_size:size},industry),[values,acv,size,industry]);

  const changed = useMemo(()=>{
    const c={};
    Object.values(variables).flat().forEach(v=>{
      if(values[v.key]!==base[v.key])c[v.key]={old:base[v.key],new:values[v.key],variable:v};
    });
    return c;
  },[values,base,variables]);
  const hasChanges = Object.keys(changed).length>0;

  const lastAnalysis = useMemo(()=>{
    if(!lastKey||!changed[lastKey])return null;
    const{old:o,new:n}=changed[lastKey];
    return analyseChange(lastKey,o,n,industry,cur,nxt);
  },[lastKey,changed,industry,cur,nxt]);

  const summary = useMemo(()=>scenarioSummary(cur,nxt,changed,industry),[cur,nxt,changed,industry]);
  const od = nxt.overall-cur.overall;
  const nrrD = nxt.nrr-cur.nrr;
  const riskD = cur.revenueAtRisk-nxt.revenueAtRisk;
  const totalARR = acv*size;

  const radarData = PILLARS.map(p=>({subject:p.label,base:cur[p.key],scenario:nxt[p.key]}));
  const barData   = PILLARS.map(p=>({name:p.label,base:cur[p.key],scenario:nxt[p.key],delta:nxt[p.key]-cur[p.key],color:p.color}));

  const allAnalyses = useMemo(()=>Object.entries(changed).map(([key,{old:o,new:n}])=>({
    key, analysis:analyseChange(key,o,n,industry,cur,nxt),
  })),[changed,industry,cur,nxt]);

  return (
    <div className={s.app}>
      <nav className={s.nav}>
        <div className={s.navBrand}>
          <img src={logo} alt="CX Lighthouse" className={s.navLogo}/>
          <div>
            <div className={s.navTitle}>ROOTS GTM Scenario Simulator</div>
            <div className={s.navSub}>by The CX Lighthouse</div>
          </div>
        </div>
        <div className={s.navCenter}>
          <span className={`${s.navPill} ${s.navPillActive}`}>Live</span>
          <span className={s.navPill}>Deterministic Engine</span>
          <span className={s.navPill}>No AI Inference</span>
        </div>
        <div className={s.navRight}>
          <a href="https://www.thecxlighthouse.com" target="_blank" rel="noopener noreferrer" className={s.navLink}>Website</a>
          <a href={BOOKING} target="_blank" rel="noopener noreferrer" className={s.navCta}>Book a Session</a>
        </div>
      </nav>

      <section className={s.hero}>
        <div className={s.heroTop}>
          <div className={s.heroLeft}>
            <div className={s.heroEyebrow}>
              <span className={s.heroEyebrowDot}/>
              <span className={s.heroEyebrowText}>ROOTS GTM Intelligence Platform</span>
            </div>
            <h1 className={s.heroTitle}>
              Model the Financial Impact of<br/>
              <span className={s.heroTitleRed}>Every Operational Decision</span><br/>
              <span className={s.heroTitleAcc}>Before You Make It</span>
            </h1>
            <p className={s.heroSub}>
              Adjust any GTM lever and see projected impact on your ROOTS score, {ind.nrrLabel}, and {ind.revenue} at risk — grounded in {industry} industry benchmarks. No AI. No guesses. Pure deterministic logic.
            </p>
          </div>
        </div>
        <div className={s.configRow}>
          <div className={s.configField}>
            <label className={s.configLabel}>Industry</label>
            <select className={s.configSelect} value={industry} onChange={e=>resetIndustry(e.target.value)}>
              {Object.keys(INDUSTRIES).map(i=><option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div className={s.configDivider}/>
          <div className={s.configField}>
            <label className={s.configLabel}>Avg value per {ind.customers.slice(0,-1)} ($)</label>
            <input type="number" className={s.configInput} value={acv} min={1000}
              onChange={e=>{const v=Number(e.target.value);setAcv(v);setValues(p=>({...p,_acv:v}));}}/>
          </div>
          <div className={s.configField}>
            <label className={s.configLabel}>Total {ind.customers}</label>
            <input type="number" className={s.configInput} value={size} min={1}
              onChange={e=>{const v=Number(e.target.value);setSize(v);setValues(p=>({...p,_size:v}));}}/>
          </div>
          <div className={s.configField}>
            <label className={s.configLabel}>Est. total {ind.revenue}</label>
            <input readOnly className={s.configInput} value={formatMoney(totalARR)} style={{color:'#607898',cursor:'default'}}/>
          </div>
          <div className={s.configSpacer}/>
          <button className={`${s.configReset} ${hasChanges?s.configResetActive:''}`}
            onClick={handleReset} disabled={!hasChanges}>
            {hasChanges?`↺ Reset ${Object.keys(changed).length} change${Object.keys(changed).length!==1?'s':''}`: '↺ Reset'}
          </button>
        </div>
      </section>

      <div className={s.main}>
        <aside className={s.slidersPanel}>
          <div className={s.slidersPanelHeader}>
            <span className={s.slidersPanelTitle}>Scenario Variables — {industry}</span>
          </div>
          {PILLARS.map(p=>{
            const vars=variables[p.key]||[];
            return (
              <PillarAccordion key={p.key} pillar={p} vars={vars}
                values={values} base={base} onChange={handleChange}
                baseScore={cur[p.key]} newScore={nxt[p.key]}
                open={openPillar===p.key}
                onToggle={()=>setOpenPillar(openPillar===p.key?null:p.key)}/>
            );
          })}
        </aside>

        <div className={s.resultsPanel}>
          {/* Scoreboard */}
          <div className={s.scoreboard}>
            <div className={s.scoreBaseCol}>
              <div className={s.scoreLabelSmall}>Baseline</div>
              <Ring score={cur.overall} size={90}/>
              <div className={s.scoreRingLabel}>ROOTS Score</div>
            </div>
            <div className={s.scoreCenterCol}>
              <div className={s.scoreDeltaArrow}>→</div>
              <div className={`${s.scoreDelta} ${od>0?s.scoreDeltaPos:od<0?s.scoreDeltaNeg:s.scoreDeltaZero}`}>
                {od>=0?'+':''}{od}
              </div>
              <div className={s.scoreDeltaLabel}>
                {hasChanges?`${Object.keys(changed).length} var${Object.keys(changed).length!==1?'s':''} changed`:'no changes'}
              </div>
            </div>
            <div className={s.scoreScenarioCol}>
              <div className={s.scoreLabelSmall}>Scenario</div>
              <Ring score={nxt.overall} size={90}/>
              <div className={s.scoreRingLabel}>ROOTS Score</div>
            </div>
            <div className={s.kpiGrid}>
              {[
                {label:ind.nrrLabel, base:`${cur.nrr}%`, scenario:`${nxt.nrr}%`, raw:nrrD, fmt:v=>`${v>=0?'+':''}${v}pt`, pos:nrrD>=0},
                {label:`${ind.revenue} at risk`, base:formatMoney(cur.revenueAtRisk), scenario:formatMoney(nxt.revenueAtRisk), raw:riskD, fmt:v=>`${v>0?'-':'+'}${formatMoney(Math.abs(v))}`, pos:riskD>=0},
                {label:`Total ${ind.revenue}`, base:formatMoney(totalARR), scenario:formatMoney(totalARR), raw:0, fmt:()=>'', pos:true},
              ].map((k,i)=>(
                <div key={i} className={s.kpiRow}>
                  <span className={s.kpiLabel}>{k.label}</span>
                  <span className={s.kpiBase}>{k.base}</span>
                  <span className={s.kpiArrow}>→</span>
                  <span className={`${s.kpiNew} ${k.raw>0&&k.pos?s.kpiNewPos:k.raw<0&&!k.pos?s.kpiNewNeg:s.kpiNewNeutral}`}>{k.scenario}</span>
                  {k.raw!==0&&<span className={`${s.kpiDelta} ${k.pos?s.kpiDeltaPos:s.kpiDeltaNeg}`}>{k.fmt(k.raw)}</span>}
                </div>
              ))}
            </div>
          </div>

          {/* Narrative */}
          <div className={`${s.narrative} ${hasChanges?s.narrativeActive:''}`}>
            <span className={s.narrativeIcon}>◈</span>
            <p className={s.narrativeText}>{summary}</p>
          </div>

          {/* Pillar mini grid */}
          <div className={s.pillarGrid}>
            {PILLARS.map(p=>{
              const bs=cur[p.key], ns=nxt[p.key], d=ns-bs;
              const col=ns>=70?'#00c97d':ns>=55?'#f5a623':'#c41c1c';
              return (
                <div key={p.key} className={s.pillarMiniCard}
                  style={{borderColor:openPillar===p.key?p.color+'40':undefined,cursor:'pointer'}}
                  onClick={()=>setOpenPillar(openPillar===p.key?null:p.key)}>
                  <div className={s.pillarMiniLetter} style={{color:p.color}}>{p.icon}</div>
                  <div className={s.pillarMiniLabel}>{p.label}</div>
                  <div className={s.pillarMiniScores}>
                    <span className={s.pillarMiniBase}>{bs}</span>
                    <span className={s.pillarMiniSep}>→</span>
                    <span className={s.pillarMiniNew} style={{color:col}}>{ns}</span>
                  </div>
                  {d!==0&&<div className={s.pillarMiniDelta}
                    style={{background:d>0?'rgba(0,201,125,0.12)':'rgba(196,28,28,0.12)',color:d>0?'#00c97d':'#c41c1c'}}>
                    {d>0?'+':''}{d}
                  </div>}
                </div>
              );
            })}
          </div>

          {/* Latest change */}
          {lastAnalysis&&(
            <div>
              <div className={s.sectionHdr}><span className={s.sectionTitle}>Latest Change Analysis</span></div>
              <AnalysisCard analysis={lastAnalysis}/>
            </div>
          )}

          {/* All changes */}
          {allAnalyses.length>1&&(
            <div>
              <div className={s.sectionHdr}>
                <span className={s.sectionTitle}>All Scenario Changes</span>
                <span className={s.sectionCount}>{allAnalyses.length} variables</span>
              </div>
              <div className={s.analysisGrid}>
                {allAnalyses.map(({key,analysis})=><AnalysisCard key={key} analysis={analysis}/>)}
              </div>
            </div>
          )}

          {/* Charts */}
          <div>
            <div className={s.chartTabs}>
              {[['radar','Radar'],['bars','By Pillar'],['delta','Impact Delta']].map(([k,l])=>(
                <button key={k} className={`${s.chartTab} ${activeChart===k?s.chartTabActive:''}`}
                  onClick={()=>setActiveChart(k)}>{l}</button>
              ))}
            </div>
            <div className={s.chartCard}>
              {activeChart==='radar'&&(
                <>
                  <div className={s.chartTitle}>ROOTS Pillar Radar — Baseline vs Scenario</div>
                  <div className={s.chartLegend}>
                    <span><span className={s.legendDot} style={{background:'#3d85f5'}}/>Baseline</span>
                    <span><span className={s.legendDot} style={{background:'#c41c1c'}}/>Scenario</span>
                  </div>
                  <ResponsiveContainer width="100%" height={260}>
                    <RadarChart data={radarData} margin={{top:10,right:20,bottom:10,left:20}}>
                      <PolarGrid stroke="#162240"/>
                      <PolarAngleAxis dataKey="subject" tick={{fill:'#607898',fontSize:11,fontWeight:600}}/>
                      <Radar dataKey="base" stroke="#3d85f5" fill="#3d85f5" fillOpacity={0.10} strokeWidth={2} name="Baseline"/>
                      <Radar dataKey="scenario" stroke="#c41c1c" fill="#c41c1c" fillOpacity={0.13} strokeWidth={2.5} name="Scenario"/>
                    </RadarChart>
                  </ResponsiveContainer>
                </>
              )}
              {activeChart==='bars'&&(
                <>
                  <div className={s.chartTitle}>Pillar Scores — Baseline vs Scenario</div>
                  <div className={s.chartLegend}>
                    <span><span className={s.legendDot} style={{background:'#3d85f5'}}/>Baseline</span>
                    <span><span className={s.legendDot} style={{background:'#c41c1c'}}/>Scenario</span>
                  </div>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={barData} barCategoryGap="32%">
                      <CartesianGrid stroke="#162240" vertical={false}/>
                      <XAxis dataKey="name" tick={{fill:'#607898',fontSize:11}} axisLine={false} tickLine={false}/>
                      <YAxis domain={[0,100]} tick={{fill:'#607898',fontSize:11}} axisLine={false} tickLine={false}/>
                      <Tooltip contentStyle={TT}/>
                      <ReferenceLine y={70} stroke="#00c97d" strokeDasharray="3 4" strokeOpacity={0.5}/>
                      <ReferenceLine y={55} stroke="#f5a623" strokeDasharray="3 4" strokeOpacity={0.5}/>
                      <Bar dataKey="base" fill="#3d85f5" radius={[3,3,0,0]} name="Baseline"/>
                      <Bar dataKey="scenario" radius={[3,3,0,0]} name="Scenario">
                        {barData.map((e,i)=><Cell key={i} fill={e.color}/>)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </>
              )}
              {activeChart==='delta'&&(
                <>
                  <div className={s.chartTitle}>Impact by Pillar</div>
                  <div className={s.chartSub}>Green = scenario improvement · Red = scenario decline.</div>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={barData} barCategoryGap="42%">
                      <CartesianGrid stroke="#162240" vertical={false}/>
                      <XAxis dataKey="name" tick={{fill:'#607898',fontSize:11}} axisLine={false} tickLine={false}/>
                      <YAxis tick={{fill:'#607898',fontSize:11}} axisLine={false} tickLine={false}/>
                      <Tooltip contentStyle={TT} formatter={v=>[`${v>=0?'+':''}${v} pts`,'Delta']}/>
                      <ReferenceLine y={0} stroke="#344e6a" strokeWidth={1.5}/>
                      <Bar dataKey="delta" radius={[3,3,0,0]} name="Delta">
                        {barData.map((e,i)=><Cell key={i} fill={e.delta>=0?'#00c97d':'#c41c1c'}/>)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </>
              )}
            </div>
          </div>

          {/* CTA */}
          <div className={s.cta}>
            <div className={s.ctaInner}>
              <img src={logo} alt="The CX Lighthouse" className={s.ctaLogo}/>
              <div className={s.ctaContent}>
                <div className={s.ctaTitle}>Ready to act on this analysis?</div>
                <p className={s.ctaText}>Book a free strategy session with The CX Lighthouse. We'll run the full ROOTS diagnostic on your real data and identify your highest-leverage first move.</p>
              </div>
              <a href={BOOKING} target="_blank" rel="noopener noreferrer" className={s.ctaBtn}>Book Strategy Session →</a>
            </div>
          </div>

          <footer className={s.footer}>
            <div className={s.footerLeft}>
              <img src={logo} alt="" className={s.footerLogo}/>
              <span className={s.footerText}>R.O.O.T.S GTM Scenario Simulator by The CX Lighthouse</span>
            </div>
            <div className={s.footerRight}>
              <a href="https://www.thecxlighthouse.com" target="_blank" rel="noopener noreferrer">thecxlighthouse.com</a>
              <span>·</span>
              <span>© 2026 CX Lighthouse Ltd.</span>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
}
